
import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import * as emailTemplateService from '@/services/emailTemplate';
import { EmailTemplate } from '@/types/emailTemplates';

interface EmailPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string | null;
}

export function EmailPreviewDialog({ open, onOpenChange, templateId }: EmailPreviewDialogProps) {
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Only fetch when both templateId is available and dialog is open
  useEffect(() => {
    if (!templateId || !open) {
      return;
    }
    
    let isMounted = true;
    const fetchTemplateData = async () => {
      try {
        setLoading(true);
        setError(null);
        const templateData = await emailTemplateService.fetchTemplate(templateId);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setTemplate(templateData);
        }
      } catch (error: any) {
        console.error("Failed to load template:", error);
        
        if (isMounted) {
          setError(error.message || 'Failed to load template');
          toast({
            variant: 'destructive',
            title: 'Error',
            description: `Failed to load template: ${error.message}`,
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchTemplateData();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [templateId, open, toast]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      // Small delay to prevent flickering during exit animation
      const timeout = setTimeout(() => {
        setTemplate(null);
        setError(null);
      }, 300);
      
      return () => clearTimeout(timeout);
    }
  }, [open]);

  // Memoize the example HTML generation
  const exampleHtml = useMemo(() => {
    if (!template) return '';

    let html = template.html_content || '';

    try {
      // Replace template variables with example values
      if (template.variables && typeof template.variables === 'object') {
        Object.keys(template.variables).forEach(key => {
          const placeholder = `{{${key}}}`;
          const exampleValue = template.variables[key] || `[Example ${key}]`;
          html = html.replace(new RegExp(placeholder, 'g'), exampleValue);
        });
      }

      // Handle conditionals in a basic way
      const conditionalRegex = /{{#if\s+([^}]+)}}([\s\S]*?)(?:{{else}}([\s\S]*?))?{{\/if}}/g;
      html = html.replace(conditionalRegex, (match, condition, thenContent, elseContent = '') => {
        // For preview purposes, show both possibilities
        return `<div style="border-left: 2px solid #4CAF50; padding-left: 8px; margin: 4px 0;">
                  <div style="color: #4CAF50; font-size: 12px;">If ${condition} is true:</div>
                  ${thenContent}
                </div>
                ${elseContent ? `<div style="border-left: 2px solid #F44336; padding-left: 8px; margin: 4px 0;">
                  <div style="color: #F44336; font-size: 12px;">If ${condition} is false:</div>
                  ${elseContent}
                </div>` : ''}`;
      });
    } catch (err) {
      console.error('Error processing preview HTML:', err);
      html = `<div style="color: red; padding: 20px;">Error generating preview: ${err.message}</div>`;
    }

    return html;
  }, [template]);

  const handleRefresh = () => {
    if (!templateId) return;
    
    setLoading(true);
    emailTemplateService.fetchTemplate(templateId)
      .then((templateData) => {
        setTemplate(templateData);
      })
      .catch((error) => {
        console.error("Failed to refresh template:", error);
        setError(error.message || 'Failed to refresh template');
        toast({
          variant: 'destructive',
          title: 'Error',
          description: `Failed to refresh template: ${error.message}`,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] w-full h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>
              {loading ? <Skeleton className="h-6 w-64" /> : template?.name || 'Email Template Preview'}
            </span>
            {!loading && template && (
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleRefresh}
                className="h-8 w-8"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="space-y-4 p-6">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center flex-1 p-4">
              <div className="text-center">
                <p className="text-destructive mb-4">{error}</p>
                <Button onClick={handleRefresh}>Try Again</Button>
              </div>
            </div>
          ) : template ? (
            <div className="flex-1 overflow-auto">
              <div className="bg-gray-100 p-2 mb-2 rounded">
                <div><strong>Subject:</strong> {template.subject}</div>
                <div><strong>Template ID:</strong> {template.template_id}</div>
              </div>
              
              <div className="border rounded-md overflow-hidden bg-white h-full">
                <iframe
                  srcDoc={exampleHtml}
                  title="Email Preview"
                  className="w-full h-[50vh]"
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center flex-1">
              <p className="text-muted-foreground">No template selected or template not found</p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
